/**
 * Column arithmetic shared by the two "Customize columns" popovers — the search
 * results page and the saved-project / team-feedback results tab.
 *
 * TD-2746 (folding TD-2530) — both surfaces carried their own copy of the
 * financials-only column sets and of the drag-reorder logic, written against their
 * own column enum. Those enums (`PROFILER_SEARCH_RESULTS_COLUMNS` and
 * `PROFILER_PROJECT_VIEW_COLUMNS`) use the same id strings for every column
 * involved here, so the arithmetic is genuinely one thing and lives here, next to
 * `profilerViews.js` which TD-2733 centralised for the same reason.
 *
 * Deliberately NOT shared: how each surface decides the toggle is *on*. The search
 * page persists a flag (`isProfilerExtendedFinancialsToggled` plus a localStorage
 * key); the project view derives it from the column set and stores nothing. Those
 * are different behaviours, not different spellings, and reconciling them is a
 * product decision rather than a refactor.
 */

// The row's control column, pinned last and never part of the sortable list.
const ACTIONS_COLUMN_ID = 'ACTIONS';

// Shown while the Financials toggle is ON. COMPANY_NAME rides along so rows stay
// identifiable, and ACTIONS because it is a control, not a metric.
export const FINANCIALS_ONLY_COLUMNS = [
  'COMPANY_NAME',
  'SALES',
  'EMPLOYEES',
  'CAGR3Y',
  'CAGR4Y',
  'EBIT_MARGIN',
  ACTIONS_COLUMN_ID
];

// Hidden when the toggle goes back OFF and there is no saved selection to restore.
// Note this is NOT the complement of the set above — COMPANY_NAME and ACTIONS are
// absent from both, so they stay visible either way.
export const FINANCIALS_METRIC_COLUMNS = ['SALES', 'EMPLOYEES', 'CAGR3Y', 'CAGR4Y', 'EBIT_MARGIN'];

/**
 * Which columns should be selected once the Financials toggle settles.
 *
 * @param {boolean} isOn the toggle's new state
 * @param {string[]|null} savedSelection ids captured before the toggle was switched
 *   on, or null/[] when nothing was stored
 * @returns {(columnId: string) => boolean} predicate for `column.selected`
 */
export const resolveFinancialsOnlySelection = (isOn, savedSelection) => {
  if (isOn) return (columnId) => FINANCIALS_ONLY_COLUMNS.includes(columnId);
  if (savedSelection?.length) return (columnId) => savedSelection.includes(columnId);
  return (columnId) => !FINANCIALS_METRIC_COLUMNS.includes(columnId);
};

/**
 * The column list saved in the browser under `key`, or null without one. A stored value that is
 * not a list (null, an object, broken JSON) counts as none, so the caller falls back to its
 * initial columns instead of failing on it.
 *
 * @param {string} key - the localStorage key of a search (pcbsrp-) or project (pcbpv-) list
 * @returns {Array<{id: string}>|null}
 */
export const readSavedProfilerColumns = (key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return Array.isArray(saved) ? saved : null;
  } catch {
    return null;
  }
};

/**
 * A column list saved in the browser plus the columns the app added since it was saved (the
 * Shareholders column, for one). A saved list is otherwise reused as it is, so a new column never
 * reached Customize columns there. The new ones join unselected, before ACTIONS, which leaves the
 * user's layout as it was. For Shareholders that is also its default: it is selected only for
 * the ownership criteria, which came with the column, and a search with other criteria is a new
 * search with its own list.
 * Returns `saved` itself when nothing is missing.
 *
 * @param {Array<{id: string}>} saved
 * @param {Array<{id: string}>} current - the columns the app has now (an initial list)
 */
export const withNewProfilerColumns = (saved, current) => {
  const savedIds = new Set(saved.map((column) => column.id));
  const added = current
    .filter((column) => !savedIds.has(column.id))
    .map((column) => ({ ...column, selected: false }));
  if (!added.length) return saved;
  const items = saved.filter((column) => column.id !== ACTIONS_COLUMN_ID);
  const actions = saved.filter((column) => column.id === ACTIONS_COLUMN_ID);
  return [...items, ...added, ...actions];
};

/**
 * Moves the column at `activeId` to the position of `overId`, keeping ACTIONS
 * pinned last. Returns a new array, or `columns` untouched if either id is unknown.
 */
export const reorderProfilerColumns = (columns, activeId, overId) => {
  const items = columns.filter((column) => column.id !== ACTIONS_COLUMN_ID);
  const oldIndex = items.findIndex((item) => item.id === activeId);
  const newIndex = items.findIndex((item) => item.id === overId);
  if (oldIndex === -1 || newIndex === -1) return columns;

  const [movedItem] = items.splice(oldIndex, 1);
  items.splice(newIndex, 0, movedItem);

  const actionsColumn = columns.find((column) => column.id === ACTIONS_COLUMN_ID);
  return [...items, actionsColumn];
};
